import { Time } from 'lightweight-charts'
import { SettingType } from './drawing.enums'
import { RectangleDrawingSettings } from './rectangleDrawingTool/rectangeDrawingTool'
import { LineDrawingSettings, LineHeadType } from './lineDrawingTool/lineDrawingTool'
import { EllipseDrawingSettings } from './ellipseDrawingTool/ellipseDrawingTool'
import { ParallelChannelDrawingSettings } from './parallelChannelDrawingTool/parallelChannelDrawingTool'
import { FlatTopBottomDrawingSettings } from './flatTopBottomDrawingTool/flatTopBottomDrawingTool'
import { DisjointChannelDrawingSettings } from './disjointChannel/disjointChannel'
import { TriangleDrawingSettings } from './triangleDrawingTool/triangleDrawingTool'
import { CircleDrawingSettings } from './circleDrawingTool/circleDrawingTool'
import { PathDrawingSettings } from './pathDrawingTool/pathDrawingTool'
import { RotatedRectangleDrawingSettings } from './rotatedRectangleTool/rotatedRectangleTool'
import { FibonacciDrawingSettings } from './fibonacciDrawingTool/fibonacciDrawingTool'
import { TrendBasedFibonacciDrawingSettings } from './trendBasedFibonacciDrawingTool/trendBasedFibonacciDrawingTool'
import { PositionForecastDrawingSettings } from './positionForecastDrawingTool/positionForecastDrawingTool'
import { IndicatorLineDrawingSettings } from './indicatorLineDrawingTool/indicatorLineDrawingTool'
import { DateRangeDrawingSettings } from './dateRangeDrawingTool/dateRangeDrawingTool'
import Chart from '../chart'
import { PriceRangeDrawingSettings } from './priceRangeDrawingTool/priceRangeDrawingTool'
import { ForecastDrawingSettings } from './forecastDrawingTool/forecastDrawingTool'
import { FibonacciCirclesDrawingSettings } from './fibonacciCirclesDrawingTool/fibonacciCirclesDrawingTool'
import { HorizontalLineDrawingSettings } from './horizontalLineTool/horizontalLineTool'
import { GannFanDrawingSettings } from './gannFanDrawingTool/gannFanDrawingTool'
import { XABCDPatternDrawingSettings } from './xabcdPatternTool/xabcdPatternTool'
import { ABCDPatternDrawingSettings } from './abcdPatternTool/abcdPatternTool'
import { TrianglePatternDrawingSettings } from './trianglePatternTool/trianglePatternTool'
import { OiProfileSettings } from './oiProfile/oiProfile'
import { VerticalLineDrawingSettings } from './verticalLineTool/verticalLineTool'
import { ThreeDivesPatternSettings } from './3DivesPatternTool/3DivesPatternTool'
import { CrossLineDrawingSettings } from './crossLineDrawingTool/crossLineDrawingTool'
import { ElliottImpulseWaveSettings } from './elliottImpulseWave/elliottImpulseWave'
import { HeadAndShoulderPatternSettings } from './headAndShoulderPatternTool/headAndShoulderPatternTool'
import { TextToolSettings } from './textTool/textTool'
import { PriceNoteSettings } from './priceNoteTool/priceNoteTool'
import { ContractSwitchDrawingSettings } from './contractSwitchTool/ContractSwitchTool'
import { CorporateActionDrawingSettings } from './corporateActionTool/CorporateActionTool'
import { OiHeatmapSettings } from './oiProfile/oiHeatmap'
import { CompanyEventDrawingSettings } from './companyEventTool/CompanyEventTool'
import { OrdersOnChartToolDrawingSettings } from './ordersOnChartTool/OrdersOnChartTool'
import { OrdersMarkersDrawingSettings } from './ordersOnChartTool/OrderMarkers'
import { BrushDrawingSettings } from './brushTool/brushTool'
import { FixedRangeVolumeProfileDrawingSettings } from './fixedRangeVolumeProfile/fixedRangeVolumeProfile'
import { AnchoredVolumeProfileDrawingSettings } from './AnchoredVolumeProfile/anchoredVolumeProfile'
import { NewsArticleDrawingSettings } from './newsArticleTool/NewsArticleTool'
import { SessionBreakDrawingSettings } from './sessionBreakTool/SessionBreakTool'
import { ChartAlertsOnChartSettings } from './chartAlertsOnChartTool/ChartAlertsOnChartTool'

export type DrawingOptions<O extends Record<string, any>, C extends Record<string, any>, S extends Record<string, any>> = {
    id: string,
    displayType: DrawingDisplayType,
    referenceDrawingId?: string,
    chart: Chart,
    options: O,
    settings: Partial<C>,
    initialState: S,
    magnetMode: boolean,
    paneId?: string,
    seriesId?: string
    onDrawingInactive: () => void,
    onDrawingActive: (drawingType: DrawingTypes) => void,
    onDestroy: (id: string) => void,
    onDrawingComplete: (id: string) => void,
    onDrawingUpdate: (id: string) => void,
    onFocusChange: (id: string, focused: boolean) => void;
}

export type Point = {
    id: string,
    x: Time,
    y: number,
    hidden?: boolean,
    coordinates?: PointCoordinates
}

export type DrawingMode = 'DRAW' | 'EDIT';

export type LabelAxisPoints = keyof Pick<LabelAxisViewConfig<any>, 'p1' | 'p2'>

export type LabelAxisViewConfig<T extends Time | number> = {
    id: string,
    p1: T,
    p2: T,
    fill: boolean,
    fillColor: string,
    labelColors: Record<LabelAxisPoints, string>
}

export interface BitmapPositionLength {
    /** coordinate for use with a bitmap rendering scope */
    position: number;
    /** length for use with a bitmap rendering scope */
    length: number;
}

export type PointCoordinates = {
    x: number,
    y: number
}

export type DrawingSettingGroup<C, V> = {
    id: string,
    displayName: string,
    settings: DrawingSettingItem<C, V>[],
    visible: boolean
}

export type DrawingSettingItem<C, V> = {
    name: keyof C,
    label: string,
    type: SettingType,
    custom: boolean,
    visible: boolean,
    defaultValue: V
    range?: number[],
    options?: Array<{ label: string, value: V }>,
    fieldOptions?: any
}

export type ColorValue = {
    color: string,
    opacity: number
}

export type LineTypes = 'normal' | 'dotted' | 'dashed'

export type SettingValues = ColorValue | LineTypes | LineHeadType | string | boolean | number | Point

export type DrawingSettingsMap = {
    'simpleIndicatorLine': IndicatorLineDrawingSettings,
    'infoIndicatorLine': IndicatorLineDrawingSettings,
    'oiHistorical': OiHeatmapSettings,
    'oiHeatmap': OiHeatmapSettings,
    'oiProfile': OiProfileSettings,
    'trendLine': LineDrawingSettings,
    'ray': LineDrawingSettings,
    'arrow': LineDrawingSettings,
    'extendedLine': LineDrawingSettings,
    'trendAngle': LineDrawingSettings,
    'infoLine': LineDrawingSettings,
    'simpleLine': LineDrawingSettings
    'parallelChannel': ParallelChannelDrawingSettings,
    'flatTopBottom': FlatTopBottomDrawingSettings,
    'disjointChannel': DisjointChannelDrawingSettings,
    'rectangle': RectangleDrawingSettings,
    'ellipse': EllipseDrawingSettings,
    'triangle': TriangleDrawingSettings,
    'circle': CircleDrawingSettings,
    'path': PathDrawingSettings,
    'polyLine': PathDrawingSettings,
    'rotatedRectangle': RotatedRectangleDrawingSettings,
    'fibonacci': FibonacciDrawingSettings,
    'trendBasedFibonacci': TrendBasedFibonacciDrawingSettings,
    'positionForecast': PositionForecastDrawingSettings,
    'dateRange': DateRangeDrawingSettings,
    'priceRange': PriceRangeDrawingSettings,
    'forecast': ForecastDrawingSettings,
    'fibonacciCircles': FibonacciCirclesDrawingSettings,
    'horizontalLine': HorizontalLineDrawingSettings,
    'horizontalRay': HorizontalLineDrawingSettings,
    'gannFan': GannFanDrawingSettings,
    'xabcdPattern': XABCDPatternDrawingSettings,
    'abcdPattern': ABCDPatternDrawingSettings,
    'trianglePattern': TrianglePatternDrawingSettings,
    'verticalLine': VerticalLineDrawingSettings,
    'crossLine': CrossLineDrawingSettings,
    '3DivesPattern': ThreeDivesPatternSettings,
    'elliottImpulseWave': ElliottImpulseWaveSettings,
    'headAndShoulderPattern': HeadAndShoulderPatternSettings,
    'text': TextToolSettings,
    'anchoredText': TextToolSettings,
    'priceNote': PriceNoteSettings,
    'zoomIn': any,
    'contractSwitch': ContractSwitchDrawingSettings
    'corporateAction': CorporateActionDrawingSettings
    'companyEvent': CompanyEventDrawingSettings,
    'chartOrdersActions': OrdersOnChartToolDrawingSettings,
    'orderMarkers': OrdersMarkersDrawingSettings,
    'newsArticle': NewsArticleDrawingSettings,
    'sessionBreak': SessionBreakDrawingSettings,
    'brush': BrushDrawingSettings,
    'replaySelection': any,
    'fixedRangeVolumeProfile': FixedRangeVolumeProfileDrawingSettings
    'anchoredVolumeProfile': AnchoredVolumeProfileDrawingSettings,
    'chartAlertsOnChart': ChartAlertsOnChartSettings
}

export type DrawingTypes = keyof DrawingSettingsMap

export type DrawingSettings<T extends DrawingTypes> = DrawingSettingsMap[T]

export enum DrawingDisplayType {
    INDICATOR = 'indicator',
    DRAWING = 'drawing',
    TOOL = 'tool'
}

export type ChartDrawingItem = {
    id: string,
    referenceDrawingId?: string,
    type: DrawingTypes,
    options: Record<string, any>,
    state: Record<string, any>,
    seriesId: string,
    paneId: string,
    displayType: DrawingDisplayType,
    settings: Record<string, any>
}

export type DrawingEvents =
    'add'
    | 'abort'
    | 'add_point'
    | 'end'
    | 'remove'
    | 'move'
    | 'focused'
    | 'update_handle'
    | 'update_settings'
    | 'sync'
    | 'unsync'
    | 'update_state'